import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import dayjs from "dayjs";
import { EnsembleConfig } from "../types/ensemble";
import { SpottingMode } from "../types/spotting";
import type { WeatherGrid } from "../types/weatherGrid";

export type { EnsembleConfig } from "../types/ensemble";

export interface SimulationParams {
  droughtFactor: number;
  /**
   * Keetch-Byram Drought Index (mm) from the forecast, or null when the run
   * has none (custom / seasonal weather, older records). The solver then
   * assumes 20 x drought factor.
   */
  droughtIndex?: number | null;
  /**
   * Grass curing, 0-100. The string "auto" asks the backend to use the
   * project's satellite estimate for the run's own date and to record on the
   * simulation that it did, which is how a run shows "satellite" rather than
   * "set by the user" afterwards.
   */
  curing: number | 'auto';
  interval: number;
  duration: number;
  simulationType: string;
  startTime: string;
  timezone?: string;
  weather: object;
  fireSpreadModel: 'New';
  /** Firebrand spotting behaviour; defaults to 'realistic' when omitted. */
  spottingMode?: SpottingMode;
  ignitionRegimeId: number | null;
  weatherLabel?: string;
  /**
   * Calendar day (YYYY-MM-DD) the hourly weather was taken from when it is
   * replayed onto a different run date. Projects with Canadian FBP fuel take
   * their fuel-moisture codes from this day rather than the run date.
   * Omitted for custom-built, seasonal and rerun weather.
   */
  weatherDate?: string;
  /** Present only for ensemble runs; omitted/undefined for a single simulation. */
  ensemble?: EnsembleConfig | null;
  /**
   * Spatial weather lattice fetched with a forecast/archive day (snake_case
   * because this object is the POST body and the backend field is
   * `weather_grid`). Omitted/undefined for custom, seasonal and rerun weather
   * so those runs stay uniform. Per-hour overrides need no handling here: the
   * edited `weather` plus the unmodified grid is enough — the backend
   * propagates the deltas to every lattice point.
   */
  weather_grid?: WeatherGrid | null;
}

interface SimulationContextType {
  simulationParams: SimulationParams | null;
  setSimulationParams: (params: SimulationParams | null) => void;

  simulationId: string | null;
  setSimulationId: (id: string | null) => void;

  isSimulationComplete: boolean;
  setIsSimulationComplete: (complete: boolean) => void;

  /**
   * The key/UUID of the simulation currently loaded in the UI (either a newly
   * generated run or a previously saved one).
   */
  currentSavedSimId: string | null;
  setCurrentSavedSimId: (id: string | null) => void;

  /**
   * Whether the loaded fire is currently animating. Owned by the Animation
   * layer; read by layers that step back while it plays (Risk From dims the
   * other ignitions).
   */
  isAnimationPlaying: boolean;
  setIsAnimationPlaying: (playing: boolean) => void;

  resetSimulation: () => void;
}

const SimulationContext = createContext<SimulationContextType>({
  simulationParams: null,
  setSimulationParams: () => {},

  simulationId: null,
  setSimulationId: () => {},

  isSimulationComplete: false,
  setIsSimulationComplete: () => {},

  currentSavedSimId: null,
  setCurrentSavedSimId: () => {},

  isAnimationPlaying: false,
  setIsAnimationPlaying: () => {},

  resetSimulation: () => {},
});

export const useSimulation = () => useContext(SimulationContext);

export const SimulationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const defaultWeather: SimulationParams = {
    simulationType: "USE_WEATHER_FORECAST",
    weather: {},
    droughtFactor: 10,
    droughtIndex: null,
    // "auto" unless the user says otherwise: the satellite estimate is a
    // better starting point than any fixed number, and the old fixed defaults
    // disagreed with each other across the app anyway.
    curing: 'auto',
    duration: 8,
    interval: (8 * 60) / 240,
    startTime: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'), // Send local time with proper timezone offset
    fireSpreadModel: 'New',
    spottingMode: 'realistic',
    ignitionRegimeId: null,
  };

  const [simulationParams, setSimulationParams] =
    useState<SimulationParams | null>(defaultWeather);
  const [simulationId, setSimulationId] = useState<string | null>(null);
  const [isSimulationComplete, setIsSimulationComplete] =
    useState<boolean>(false);
  const [currentSavedSimId, setCurrentSavedSimId] = useState<string | null>(null);
  const [isAnimationPlaying, setIsAnimationPlaying] = useState<boolean>(false);

  const resetSimulation = useCallback(() => {
    setSimulationId(null);
    setIsSimulationComplete(false);
    setCurrentSavedSimId(null);
  }, []);

  const value = useMemo(
    () => ({
      simulationParams,
      setSimulationParams,

      simulationId,
      setSimulationId,

      isSimulationComplete,
      setIsSimulationComplete,

      currentSavedSimId,
      setCurrentSavedSimId,

      isAnimationPlaying,
      setIsAnimationPlaying,

      resetSimulation,
    }),
    [
      simulationParams,
      simulationId,
      isSimulationComplete,
      currentSavedSimId,
      isAnimationPlaying,
      resetSimulation,
    ],
  );

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
};

export default SimulationContext;
